import { type RefObject, useEffect, useRef } from 'react';

/** How far a finger moves before a drag is horizontal or vertical. */
const SLOP = 8;
/** How long after a drag its click may come (and is swallowed), in milliseconds. */
const CLICK_WINDOW_MS = 600;

/** Whether a released drag commits: past a third of the screen, or flicked. */
export function releasesSwipe(input: { dx: number; width: number; velocity: number }): boolean {
  const distance = Math.abs(input.dx);
  return (
    distance > input.width / 3 ||
    (distance > SLOP * 3 &&
      Math.sign(input.velocity) === Math.sign(input.dx) &&
      Math.abs(input.velocity) > 0.5)
  );
}

/** Whether a touch starts on content scrolling sideways (code, a table), which keeps the swipe. */
function scrollsSideways(target: EventTarget | null): boolean {
  for (
    let element = target instanceof Element ? target : null;
    element !== null;
    element = element.parentElement
  ) {
    if (element.scrollWidth > element.clientWidth + 1) {
      const overflow = getComputedStyle(element).overflowX;
      if (overflow === 'auto' || overflow === 'scroll') return true;
    }
  }
  return false;
}

/** Whether a touch starts in a field or on a slider (its finger edits it). */
function inField(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="slider"]',
    ) !== null
  );
}

/** Whether a swipe may start anywhere on a screen: not on sideways content or in a field. */
export function swipesFrom(target: EventTarget | null): boolean {
  return !scrollsSideways(target) && !inField(target);
}

export type HorizontalDrag = {
  enabled: boolean;
  /** Whether a touch starting here (its event, its x) may drag. */
  accepts: (event: TouchEvent, x: number) => boolean;
  /** Whether the drag goes this way (the sign of `dx`); another way lets the finger go. */
  allows: (dx: number) => boolean;
  /** The finger moved, `dx` from where it started (only the allowed way). */
  onDrag: (dx: number) => void;
  /** The finger lifted after a drag, with its speed in pixels per millisecond. */
  onRelease: (dx: number, velocity: number) => void;
  /** The drag was cancelled (the system took the touch). */
  onCancel: () => void;
};

/**
 * Follows a one-finger horizontal drag on an element: a vertical move first lets it scroll, a
 * move the wrong way lets an element around it take the drag. The click a drag ends on is
 * swallowed (a row dragged aside does not open).
 */
export function useHorizontalDrag(ref: RefObject<HTMLElement | null>, drag: HorizontalDrag): void {
  const current = useRef(drag);
  current.current = drag;
  const { enabled } = drag;
  // Until when the click a drag ends on is swallowed: kept while a drag that ended turns the
  // drag off (a row read on a swipe), or that click would open it.
  const swallowClickUntil = useRef(0);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const onClick = (event: MouseEvent) => {
      if (event.timeStamp > swallowClickUntil.current) return;
      swallowClickUntil.current = 0;
      event.preventDefault();
      event.stopPropagation();
    };
    element.addEventListener('click', onClick, { capture: true });
    return () => element.removeEventListener('click', onClick, { capture: true });
  }, [ref]);

  useEffect(() => {
    const element = ref.current;
    if (element === null || !enabled) return;
    let start: { x: number; y: number; time: number } | null = null;
    let dragging = false;
    const onStart = (event: TouchEvent) => {
      // A second finger ends a drag: it settles where it was.
      if (dragging) current.current.onCancel();
      const touch = event.touches[0];
      start =
        event.touches.length === 1 &&
        touch !== undefined &&
        current.current.accepts(event, touch.clientX)
          ? { x: touch.clientX, y: touch.clientY, time: event.timeStamp }
          : null;
      dragging = false;
      swallowClickUntil.current = 0;
    };
    const onMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (start === null || touch === undefined) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      if (!dragging) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        if (Math.abs(dy) >= Math.abs(dx) || !current.current.allows(dx)) {
          start = null;
          return;
        }
        dragging = true;
      }
      current.current.onDrag(current.current.allows(dx) ? dx : 0);
    };
    const onEnd = (event: TouchEvent) => {
      const began = start;
      start = null;
      if (began === null || !dragging) return;
      dragging = false;
      swallowClickUntil.current = event.timeStamp + CLICK_WINDOW_MS;
      const touch = event.changedTouches[0];
      const moved = (touch?.clientX ?? began.x) - began.x;
      const dx = current.current.allows(moved) ? moved : 0;
      current.current.onRelease(dx, dx / Math.max(1, event.timeStamp - began.time));
    };
    const onCancel = () => {
      start = null;
      if (dragging) current.current.onCancel();
      dragging = false;
    };
    element.addEventListener('touchstart', onStart, { passive: true });
    element.addEventListener('touchmove', onMove, { passive: true });
    element.addEventListener('touchend', onEnd, { passive: true });
    element.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      element.removeEventListener('touchstart', onStart);
      element.removeEventListener('touchmove', onMove);
      element.removeEventListener('touchend', onEnd);
      element.removeEventListener('touchcancel', onCancel);
    };
  }, [ref, enabled]);
}
